import { FC, useEffect, useMemo } from 'react'
import { useFormik } from 'formik'
import {
  AtendimentoType,
  ButtonType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { ValorEmpresaDtoCadastrar, ValorEmpresaView } from '../../core/_models'
import { empresaValorSchema } from './validation/empresaValorSchema'
import { createValoresByEmpresa } from '../../core/_requests'
import { getTiposAtendimentosByName } from '../../../../../../../_metronic/helpers/crud-helper/_request'
import { useT } from '_metronic/i18n/Metronici18n'

type IProps = { idEmpresa: number }

export const EmpresaValoresModal: FC<IProps> = ({ idEmpresa }) => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: valorEmpresa } = (fetchStates['valorEmpresa'] || {}) as FetchState<ValorEmpresaView>

  const initialValues = {
    tipoAtendimento: undefined as IOption<AtendimentoType> | undefined,
    especialidades: valorEmpresa?.especialidade
      ? [{ label: valorEmpresa.especialidade.nome, value: valorEmpresa.especialidade.id }]
      : ([] as IOption<number>[]),
    valor: valorEmpresa?.valor,
    valorRetorno: valorEmpresa?.valorRetorno,
    minimo: valorEmpresa?.faturamentoMinimo,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => empresaValorSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      const especialidades =
        values.especialidades.length > 0
          ? values.especialidades.map((especialidade) => especialidade.value)
          : null

      try {
        const data: ValorEmpresaDtoCadastrar = {
          tipo: values.tipoAtendimento?.value as AtendimentoType,
          especialidades,
          valor: Number(values.valor),
          valorRetorno: values.valorRetorno,
          minimo: values.minimo || null,
        }
        await createValoresByEmpresa(idEmpresa, data)
        await showAlert({
          title: 'Sucesso!',
          text: `Valor ${valorEmpresa ? 'atualizado' : 'cadastrado'} com sucesso!`,
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { setFieldValue } = formik

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (valorEmpresa?.tipo) {
          const data = await getTiposAtendimentosByName(valorEmpresa.tipo)
          setFieldValue('tipoAtendimento', { label: data.descricao, value: data.name })
        }
      } catch (error) {
        showError(error)
      }
    }

    fetchData()
  }, [setFieldValue, valorEmpresa?.tipo])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row'>
        <FormSelect
          formik={formik}
          name='especialidades'
          entity='especialidade'
          placeholder='Selecione especialidades (ou deixe vazio para todas)'
          fetchOptions={OptionsHelper.getOptionsEspecialidades}
          isDisabled={formik.isSubmitting || !!valorEmpresa}
          infoText='Se não for informado, valor será cadastrado para o tipo de atendimento'
          className='mb-3'
          isMulti
          highlight
        />
        <div className='col-lg-6'>
          <FormSelect
            formik={formik}
            name='tipoAtendimento'
            entity='tipoAtendimento'
            fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
            isDisabled={formik.isSubmitting || !!valorEmpresa}
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-lg-6'>
          <FormInput
            formik={formik}
            name='minimo'
            type='number'
            placeholder='Mínimo de atendimentos'
            label='Mínimo de atendimentos mensais'
            infoText='Digite 0 ou deixe em branco para não ter mínimo'
            className='mb-3'
            highlight
          />
        </div>
        <div className='col-lg-6'>
          <FormInput
            formik={formik}
            name='valor'
            type='number'
            entity='valor'
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-lg-6'>
          <FormInput
            formik={formik}
            name='valorRetorno'
            type='number'
            entity='valor_retorno'
            className='mb-3'
          />
        </div>
      </div>

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
